/**
 * <Seo /> — per-page meta tags via react-helmet-async.
 *
 * Renders <title>, meta description, canonical, Open Graph, Twitter Card,
 * hreflang and (optionally) a JSON-LD structured-data block.
 *
 * The React app is a SPA hosted at neuracom.ai, so we always emit a canonical
 * pointing to the production origin — even when a page is loaded from the
 * preview URL, Google will follow the canonical and only index neuracom.ai.
 *
 * Usage:
 *   <Seo
 *     title="Pricing — NEURACOM"
 *     description="..."
 *     canonicalPath="/pricing"
 *     ogImage="/brand/og-pricing.png"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Product", ... }}
 *   />
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { useI18n } from "@/contexts/I18nContext";

const PRODUCTION_ORIGIN = "https://neuracom.ai";
const SUPPORTED_LOCALES = ["en", "it", "pl"];
const DEFAULT_OG_IMAGE = "/brand/og-default.png";

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return PRODUCTION_ORIGIN + "/";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${PRODUCTION_ORIGIN}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/**
 * Renders per-page SEO meta.
 *
 * @param {object} props
 * @param {string} props.title             Full title including brand.
 * @param {string} props.description       120-160 char meta description.
 * @param {string} props.canonicalPath     Path only, e.g. "/pricing".
 * @param {string} [props.ogImage]         Absolute path or URL for og:image (1200x630 ideal).
 * @param {string} [props.ogType]          "website" (default) or "article" | "product".
 * @param {object} [props.jsonLd]          Structured-data object to serialize.
 * @param {object[]} [props.jsonLdList]    Optional array of multiple JSON-LD blocks.
 * @param {boolean} [props.noindex]        When true → robots noindex, follow.
 */
export function Seo({
  title,
  description,
  canonicalPath = "/",
  ogImage,
  ogType = "website",
  jsonLd,
  jsonLdList,
  noindex = false,
}) {
  const { locale } = useI18n();
  const canonical = absoluteUrl(canonicalPath);
  const image = absoluteUrl(ogImage || DEFAULT_OG_IMAGE);
  const blocks = jsonLdList || (jsonLd ? [jsonLd] : []);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={locale} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* hreflang alternates — same URL for every supported locale (client-side switcher) */}
      {SUPPORTED_LOCALES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={canonical} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="NEURACOM by TelcaVoIP" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale === "en" ? "en_US" : locale === "it" ? "it_IT" : "pl_PL"} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/* ─────────────────── Shared JSON-LD builders ─────────────────── */

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NEURACOM by TelcaVoIP",
  url: PRODUCTION_ORIGIN,
  logo: `${PRODUCTION_ORIGIN}/brand/pwa/icon-512.png`,
  sameAs: [
    "https://www.linkedin.com/company/telcavoip",
    "https://twitter.com/telcavoip",
  ],
  description:
    "AI platform for European businesses. Multi-model chat, image generation, voice, code, document analysis. Legal Pro tier for Italian lawyers with 103-ruling jurisprudence database.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "IT",
    addressLocality: "Genova",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "hello@telcavoip.eu",
    availableLanguage: ["Italian", "English", "Polish"],
  },
};

export const softwareApplicationJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "NEURACOM",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS (PWA), Android (PWA), Windows",
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "EUR",
    lowPrice: "0",
    highPrice: "149",
    offerCount: "4",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "127",
  },
  description:
    "Multi-model AI workspace with chat (Claude, GPT, Gemini), image generation (Nano Banana + GPT Image 1), voice transcription, code builder, document analysis. GDPR compliant, EU-hosted. Legal Pro tier for Italian lawyers.",
};

export function breadcrumbJsonLd(items /* [{name, path}] */) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absoluteUrl(it.path),
    })),
  };
}

export function productJsonLd({ name, description, image, price, currency = "EUR", url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image: image ? absoluteUrl(image) : `${PRODUCTION_ORIGIN}${DEFAULT_OG_IMAGE}`,
    offers: {
      "@type": "Offer",
      price: String(price),
      priceCurrency: currency,
      availability: "https://schema.org/InStock",
      url: absoluteUrl(url),
    },
  };
}
